import { useCallback, useMemo } from 'react'
import { Pressable, View } from 'react-native'
import Animated, { FadeIn, LinearTransition } from 'react-native-reanimated'
import { Fragment } from 'react/jsx-runtime'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import { cn } from '~/lib/utils'

export type InputSegment<T extends string> = {
  name: T
  value: unknown
  editor: React.ReactNode
  isValid: () => boolean
  hasError?: () => boolean
  renderValue: () => React.ReactNode
  optional?: boolean
  readOnly?: boolean
}

export type ScriptSegment<T extends string> = {
  name: T
  segmentScript: string
  firstSegmentScript?: string
  lastSegmentScript?: string
  buttonLabel: string
}

type ParsedSegment<T extends string> = { variant: 'required' | 'optional' } & (
  | {
      type: 'text'
      name: T
      text: string
      firstText?: string
      lastText?: string
    }
  | { type: 'editor'; name: T }
  | { type: 'button'; name: T; label: string }
)

export type GuidedFormSummaryDisplayMode = 'edit' | 'display-all' | 'display-valid'

type GuidedFormSummaryProps<T extends string> = {
  inputs: InputSegment<T>[]
  script: ScriptSegment<T>[]
  activatedSegments: T[]
  selectedSegment?: T
  onSelectSegment?: (name: T | undefined) => void
  renderEditor?: (name: T) => React.ReactNode
  displayMode?: GuidedFormSummaryDisplayMode
  className?: string
  textClassName?: string
  buttonClassName?: string
  segmentContainerClassName?: string
  buttonsContainerClassName?: string
  prefixScript?: string
  suffixScript?: string
}

const EDITOR_TOKEN = '[[editor]]'

function parseScriptSegment<T extends string>({
  scriptSegment,
  variant = 'required',
  isFirst = false,
  isLast = false,
}: {
  scriptSegment: ScriptSegment<T>
  variant?: 'required' | 'optional'
  isFirst?: boolean
  isLast?: boolean
}): ParsedSegment<T>[] {
  const text =
    (isFirst
      ? scriptSegment.firstSegmentScript
      : isLast
        ? scriptSegment.lastSegmentScript
        : scriptSegment.segmentScript) ?? scriptSegment.segmentScript

  const parts = text.split(EDITOR_TOKEN)
  const result: ParsedSegment<T>[] = []

  for (let index = 0; index < parts.length; index++) {
    const part = parts[index]

    if (part.trim().length > 0) {
      result.push({
        type: 'text',
        name: scriptSegment.name,
        text: part,
        variant,
      })
    }

    if (index < parts.length - 1) {
      result.push({ type: 'editor', name: scriptSegment.name, variant })
    }
  }

  return result
}

export function GuidedFormSummary<T extends string>({
  inputs,
  script,
  activatedSegments,
  selectedSegment,
  onSelectSegment,
  renderEditor,
  displayMode = 'edit',
  className,
  textClassName,
  buttonClassName,
  segmentContainerClassName,
  buttonsContainerClassName,
  prefixScript,
  suffixScript,
}: GuidedFormSummaryProps<T>) {
  const activatedSet = useMemo(() => new Set(activatedSegments), [activatedSegments])

  const inputsByName = useMemo(() => new Map(inputs.map((input) => [input.name, input])), [inputs])

  const requiredScript = useMemo(
    () => script.filter((segment) => !inputsByName.get(segment.name)?.optional),
    [script, inputsByName]
  )

  const optionalScript = useMemo(
    () => script.filter((segment) => inputsByName.get(segment.name)?.optional),
    [script, inputsByName]
  )

  const allRequiredValid = useMemo(() => {
    return requiredScript.every((segment) => {
      const input = inputsByName.get(segment.name)

      if (!input) {
        return false
      }

      const isReadOnly = input.readOnly === true
      const isActivated = isReadOnly || activatedSet.has(segment.name)

      return isActivated && input.isValid()
    })
  }, [requiredScript, inputsByName, activatedSet])

  const getNextRequiredSegment = useCallback((): T | null => {
    for (const segment of requiredScript) {
      const input = inputsByName.get(segment.name)
      const isReadOnly = input?.readOnly === true

      if (!isReadOnly && !activatedSet.has(segment.name)) {
        return segment.name
      }
    }

    return null
  }, [requiredScript, activatedSet, inputsByName])

  const EditorButton = useCallback(
    ({ name, children, className }: { name: T; children: React.ReactNode; className?: string }) => {
      const input = inputsByName.get(name)
      const isDisabled = displayMode !== 'edit' || input?.readOnly === true
      const isError = !isDisabled && (input?.hasError?.() ?? false)

      return (
        <Pressable
          testID={`guided-form-editor-button-${name}`}
          onPress={() => onSelectSegment?.(name)}
          disabled={isDisabled}
          aria-readonly={isDisabled}
          className={cn(
            'group/button flex max-w-full flex-row items-baseline self-center rounded-md bg-tertiary-foreground/90 px-2 py-2',
            !isDisabled &&
              'transition-all duration-150 hover:bg-muted active:scale-95 active:bg-muted',
            isDisabled && 'bg-muted py-0.5',
            isError && 'border border-destructive/40 bg-destructive/10',
            className
          )}>
          {typeof children === 'string' ? (
            <Text
              className={cn(
                'whitespace-normal text-pretty font-medium text-xl leading-normal',
                isDisabled && 'font-normal',
                !isDisabled && 'underline decoration-dotted underline-offset-4',
                isError && 'text-destructive decoration-destructive/60',
                selectedSegment === name && 'text-foreground group-hover/button:text-foreground',
                textClassName
              )}>
              {children}
            </Text>
          ) : (
            children
          )}
        </Pressable>
      )
    },
    [onSelectSegment, selectedSegment, displayMode, textClassName, inputsByName]
  )

  const buildRequiredSegments = useCallback((): ScriptSegment<T>[] => {
    const result: ScriptSegment<T>[] = []

    if (displayMode === 'display-all') {
      return requiredScript
    }

    for (const scriptSegment of requiredScript) {
      if (
        displayMode === 'display-valid' &&
        !!inputsByName.get(scriptSegment.name)?.renderValue()
      ) {
        result.push(scriptSegment)
        continue
      }

      const input = inputsByName.get(scriptSegment.name)
      const isReadOnly = input?.readOnly === true

      const isNextToActivate = scriptSegment.name === getNextRequiredSegment()

      const isActivated =
        displayMode !== 'edit' || isReadOnly || activatedSet.has(scriptSegment.name)

      if (isActivated || isNextToActivate) {
        result.push(scriptSegment)
      }
    }

    return result
  }, [requiredScript, activatedSet, displayMode, inputsByName, getNextRequiredSegment])

  const buildOptionalSegments = useCallback((): ScriptSegment<T>[] => {
    if (displayMode === 'display-all') {
      return optionalScript
    }

    if (!allRequiredValid) {
      return []
    }

    return optionalScript.filter((scriptSegment) => {
      const hasValidValue = !!inputsByName.get(scriptSegment.name)?.renderValue()

      if (displayMode === 'display-valid' && hasValidValue) {
        return true
      }

      const isActivated = activatedSet.has(scriptSegment.name)

      if (!isActivated) {
        return false
      }

      if (selectedSegment !== scriptSegment.name && !hasValidValue) {
        return false
      }

      return true
    })
  }, [allRequiredValid, optionalScript, displayMode, activatedSet, inputsByName, selectedSegment])

  const requiredSegments = buildRequiredSegments()
  const optionalSegments = buildOptionalSegments()

  const splitBySelected = useCallback(
    (segments: ParsedSegment<T>[]) => {
      if (!selectedSegment) {
        return { before: segments, after: [] as ParsedSegment<T>[] }
      }

      const index = segments.findIndex(
        (segment) => segment.type === 'editor' && segment.name === selectedSegment
      )

      if (index === -1) {
        return { before: segments, after: [] as ParsedSegment<T>[] }
      }

      return {
        before: segments.slice(0, index),
        after: segments
          .slice(index + 1)
          .filter((segment) => segment.type !== 'text' || segment.text.trim().length >= 3),
      }
    },
    [selectedSegment]
  )

  const renderParsedSegments = useCallback(
    (segments: ParsedSegment<T>[]) => {
      const segmentGroups = segments.reduce<
        {
          name: T
          level: 'required' | 'optional'
          segments: ParsedSegment<T>[]
        }[]
      >((result, segment) => {
        const prevSegment = result.at(-1)

        if (prevSegment?.name !== segment.name) {
          result.push({
            name: segment.name,
            level: segment.variant,
            segments: [segment],
          })
        } else {
          prevSegment.segments.push(segment)
        }

        return result
      }, [])

      return segmentGroups.flatMap((group) => {
        const output = []

        output.push(
          ...group.segments.map((segment) => {
            if (segment.type === 'button' && segment.variant === 'required') {
              output.push(
                <View
                  key={`btn-${segment.name}`}
                  className="w-full flex-1 basis-full items-center justify-center">
                  <Button
                    testID={`guided-form-required-button-${segment.name}`}
                    variant="primary"
                    size="lg"
                    className="mx-auto w-auto self-center"
                    onPress={() => onSelectSegment?.(segment.name)}>
                    <ButtonLabel className="text-xl">{segment.label}</ButtonLabel>
                  </Button>
                </View>
              )
            }

            if (segment.type === 'text' && segment.text.trim().length > 0) {
              output.push(
                <Text
                  key={`txt-${segment.name}-${segment.text.slice(0, 10)}`}
                  className={cn(
                    'mr-1.5 text-pretty font-light text-2xl leading-loose empty:hidden',
                    displayMode !== 'edit' && 'font-light text-xl text-primary',
                    textClassName
                  )}>
                  {segment.text.trim()}
                </Text>
              )
            }

            if (segment.type === 'editor') {
              const input = inputsByName.get(segment.name)

              if (input) {
                output.push(
                  <Fragment key={`editor-${segment.name}`}>
                    <EditorButton
                      name={segment.name}
                      className={cn('my-0.5 mr-1 inline-block', buttonClassName)}>
                      {input.renderValue()}
                    </EditorButton>
                  </Fragment>
                )
              }
            }
          })
        )

        return output
      })
    },
    [displayMode, textClassName, inputsByName, EditorButton, onSelectSegment, buttonClassName]
  )

  const renderSegmentLayout = useCallback(
    (segments: ParsedSegment<T>[]) => {
      if (displayMode !== 'edit') {
        return <>{renderParsedSegments(segments)}</>
      }

      const { before, after } = splitBySelected(segments)

      const editorNames = segments
        .filter((segment) => segment.type === 'editor')
        .map((segment) => (segment as { type: 'editor'; name: T }).name)

      const shouldRenderEditor =
        selectedSegment && editorNames.includes(selectedSegment) && renderEditor

      return (
        <>
          {renderParsedSegments(before)}
          {shouldRenderEditor ? (
            <View className={cn('relative z-50 flex w-full flex-1 basis-full')}>
              {renderEditor(selectedSegment)}
            </View>
          ) : null}
          {after.length > 0 && shouldRenderEditor ? renderParsedSegments(after) : null}
        </>
      )
    },
    [displayMode, splitBySelected, selectedSegment, renderEditor, renderParsedSegments]
  )

  const renderOptionalButtons = useCallback(() => {
    if (displayMode !== 'edit' || !allRequiredValid || optionalScript.length === 0) {
      return null
    }

    return (
      <>
        {optionalScript.map((segment) => {
          if (selectedSegment === segment.name) {
            return null
          }

          const input = inputsByName.get(segment.name)

          if (input?.isValid()) {
            return null
          }

          return (
            <Button
              key={`opt-${segment.name}`}
              testID={`guided-form-optional-button-${segment.name}`}
              variant="outline"
              size="sm"
              onPress={() => onSelectSegment?.(segment.name)}>
              <ButtonLabel>{segment.buttonLabel}</ButtonLabel>
            </Button>
          )
        })}
      </>
    )
  }, [
    displayMode,
    allRequiredValid,
    optionalScript,
    inputsByName,
    selectedSegment,
    onSelectSegment,
  ])

  const allSegments = useMemo(
    () => [
      ...(prefixScript
        ? [
            {
              type: 'text' as const,
              name: 'prefix' as T,
              text: prefixScript,
              variant: 'required' as const,
            },
          ]
        : []),
      ...requiredSegments.reduce<ParsedSegment<T>[]>((result, segment, index) => {
        if (displayMode !== 'edit') {
          result.push(
            ...parseScriptSegment({
              scriptSegment: segment,
              variant: 'required',
              isFirst: index === 0,
              isLast: index === requiredSegments.length - 1,
            })
          )

          return result
        }

        const nextRequired = getNextRequiredSegment()
        const isNextToActivate = segment.name === nextRequired

        if (isNextToActivate) {
          result.push({
            type: 'button',
            name: segment.name,
            label: segment.buttonLabel,
            variant: 'required',
          })
        } else if (!result.some((s) => s.type === 'button' && s.name === nextRequired)) {
          result.push(
            ...parseScriptSegment({
              scriptSegment: segment,
              variant: 'required',
              isFirst: index === 0,
              isLast: index === requiredSegments.length - 1,
            })
          )
        }

        return result
      }, []),
      ...optionalSegments.flatMap((segment, index) => {
        if (displayMode === 'display-valid' && !inputsByName.get(segment.name)?.renderValue()) {
          return []
        }

        return parseScriptSegment({
          scriptSegment: segment,
          variant: 'optional',
          isFirst: index === 0,
          isLast: index === optionalSegments.length - 1,
        })
      }),
      ...(suffixScript
        ? [
            {
              type: 'text' as const,
              name: 'suffix' as T,
              text: suffixScript,
              variant: 'required' as const,
            },
          ]
        : []),
    ],
    [
      prefixScript,
      requiredSegments,
      optionalSegments,
      suffixScript,
      getNextRequiredSegment,
      displayMode,
      inputsByName,
    ]
  )

  const optionalButtons = renderOptionalButtons()

  return (
    <Animated.View
      entering={FadeIn.duration(150)}
      layout={LinearTransition.duration(150)}
      className={cn('relative isolate flex-1 justify-start gap-4', className)}>
      {allSegments.length > 0 ? (
        <Text
          className={cn(
            'relative z-10 flex-1 flex-row flex-wrap items-baseline gap-x-2 gap-y-0',
            segmentContainerClassName
          )}>
          {renderSegmentLayout(allSegments)}
        </Text>
      ) : null}
      {optionalButtons ? (
        <View
          className={cn(
            'relative z-0 flex-row flex-wrap gap-x-1.5 gap-y-2',
            buttonsContainerClassName
          )}>
          {optionalButtons}
        </View>
      ) : null}
    </Animated.View>
  )
}
